import React, {useCallback, useEffect, useMemo, useRef, useState, useSyncExternalStore} from 'react'

import type Artwork from '../../webgl/Artwork'
import {
  type BrandedDividerRegistration,
  type DitherRegistration,
  type EnterpriseHeroRegistration,
  WebGLArtworkContext,
  WebGLContext,
} from '../WebGLContext/WebGLContext'

interface WebGLPageProps {
  children: React.ReactNode
  mode?: 'light' | 'dark'
}

export function WebGLPage({children, mode = 'light'}: WebGLPageProps) {
  const [dithers, setDithers] = useState<DitherRegistration[]>([])
  const [brandeddividers, setBrandedDividers] = useState<BrandedDividerRegistration[]>([])
  const registrationsRef = useRef<{
    dithers: DitherRegistration[]
    brandedDividers: BrandedDividerRegistration[]
    enterpriseHero: EnterpriseHeroRegistration | null
  }>({dithers: [], brandedDividers: [], enterpriseHero: null})
  const artworkStoreRef = useRef({artwork: null as Artwork | null, listeners: new Set<() => void>()})

  const subscribeToArtwork = useCallback((listener: () => void) => {
    artworkStoreRef.current.listeners.add(listener)
    return () => artworkStoreRef.current.listeners.delete(listener)
  }, [])

  const artwork = useSyncExternalStore(
    subscribeToArtwork,
    () => artworkStoreRef.current.artwork,
    () => null,
  )

  const registerDither = useCallback((registration: DitherRegistration) => {
    setDithers(prev => {
      // Check if already registered
      if (prev.find(d => d.id === registration.id)) return prev

      return [...prev, registration]
    })
  }, [])

  const unregisterDither = useCallback((id: string) => {
    setDithers(prev => {
      return prev.filter(dither => dither.id !== id)
    })
  }, [])

  const registerBrandedDivider = useCallback((registration: BrandedDividerRegistration) => {
    setBrandedDividers(prev => {
      // Check if already registered
      if (prev.some(s => s.id === registration.id)) return prev
      return [...prev, registration]
    })
  }, [])

  const unregisterBrandedDivider = useCallback((id: string) => {
    setBrandedDividers(prev => {
      return prev.filter(brandeddivider => brandeddivider.id !== id)
    })
  }, [])

  const registerEnterpriseHero = useCallback((registration: EnterpriseHeroRegistration) => {
    if (registrationsRef.current.enterpriseHero) return

    registrationsRef.current.enterpriseHero = registration
    artworkRef.current?.updateRegisterEnterpriseHero(registration)
  }, [])

  const unregisterEnterpriseHero = useCallback((id: string) => {
    if (registrationsRef.current.enterpriseHero?.id !== id) return

    registrationsRef.current.enterpriseHero = null
    artworkRef.current?.unregisterEnterpriseHero()
  }, [])

  const canvasRef = useRef<HTMLCanvasElement>(null)
  const artworkRef = useRef<Artwork | null>(null)
  const rAFRef = useRef<number | null>(null)

  useEffect(() => {
    if (canvasRef.current) {
      let isCancelled = false

      const publishArtwork = (nextArtwork: Artwork | null) => {
        artworkStoreRef.current.artwork = nextArtwork
        for (const listener of artworkStoreRef.current.listeners) {
          listener()
        }
      }

      const handleResize = () => {
        if (artworkRef.current && canvasRef.current) {
          const width = document.body.clientWidth
          const height = window.innerHeight
          artworkRef.current.resize(width, height)
        }
      }

      const handleRaf = () => {
        artworkRef.current?.update()
        rAFRef.current = requestAnimationFrame(handleRaf)
      }

      const setup = async () => {
        const {default: Artwork} = await import('../../webgl/Artwork')
        if (isCancelled) return
        // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
        const nextArtwork = new Artwork({canvas: canvasRef.current!})
        artworkRef.current = nextArtwork
        publishArtwork(nextArtwork)

        if (mode === 'light') {
          nextArtwork.switchBackgroundColor({isWhite: true})
        } else {
          nextArtwork.switchBackgroundColor({isWhite: false})
        }

        nextArtwork.updateRegisterDither(registrationsRef.current.dithers)
        nextArtwork.updateRegisterBrandedDivider(registrationsRef.current.brandedDividers)
        if (registrationsRef.current.enterpriseHero) {
          nextArtwork.updateRegisterEnterpriseHero(registrationsRef.current.enterpriseHero)
        }

        handleRaf()
        handleResize()

        void (async () => {
          await document.fonts?.ready
          handleResize()
        })()
      }
      setup()

      window.addEventListener('resize', handleResize)

      return () => {
        isCancelled = true
        artworkRef.current = null
        publishArtwork(null)
        window.removeEventListener('resize', handleResize)
        if (rAFRef.current) {
          cancelAnimationFrame(rAFRef.current)
        }
      }
    }
  }, [])

  useEffect(() => {
    registrationsRef.current.dithers = dithers
    if (artworkRef.current) {
      artworkRef.current.updateRegisterDither(dithers)
    }
  }, [dithers])

  useEffect(() => {
    registrationsRef.current.brandedDividers = brandeddividers
    if (artworkRef.current) {
      artworkRef.current.updateRegisterBrandedDivider(brandeddividers)
    }
  }, [brandeddividers])

  const contextValue = useMemo(
    () => ({
      registerDither,
      unregisterDither,
      registerBrandedDivider,
      unregisterBrandedDivider,
      registerEnterpriseHero,
      unregisterEnterpriseHero,
    }),
    [
      registerDither,
      unregisterDither,
      registerBrandedDivider,
      unregisterBrandedDivider,
      registerEnterpriseHero,
      unregisterEnterpriseHero,
    ],
  )

  return (
    <WebGLContext value={contextValue}>
      <WebGLArtworkContext value={artwork}>
        <div
          style={{
            position: 'relative',
            overflow: 'hidden',
          }}
        >
          <canvas
            ref={canvasRef}
            style={{
              pointerEvents: 'none',
              position: 'absolute',
              left: 0,
              zIndex: 0,
            }}
            aria-hidden="true"
            tabIndex={-1}
          />
          {children}
        </div>
      </WebGLArtworkContext>
    </WebGLContext>
  )
}
